import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

// 扩展 dayjs 插件
dayjs.extend(duration);

/**
 * 格式化秒数为可读的时间段字符串的参数接口
 */
interface FormatDurationOptions {
  /**
   * 当前语言环境,用于决定使用中文还是英文单位
   * @default 'zh_cn'
   */
  lang?: string;
}

/**
 * 将秒数格式化为可读的时间格式,支持多语言
 * @param seconds - 秒数
 * @param options - 格式化选项
 * @returns 格式化后的时间字符串
 * @example
 * formatDuration(30) => "30秒"
 * formatDuration(90) => "1分30秒"
 * formatDuration(60) => "1分"
 * formatDuration(3600) => "1时"
 * formatDuration(3661) => "1时1分1秒"
 * formatDuration(86400) => "1天"
 * formatDuration(90061) => "1天1时1分1秒"
 * formatDuration(30, { lang: 'en' }) => "30s"
 * formatDuration(90, { lang: 'en' }) => "1m 30s"
 * formatDuration(3661, { lang: 'en' }) => "1h 1m 1s"
 */
export function formatDuration(
  seconds: number | undefined,
  options: FormatDurationOptions = {},
): string {
  const { lang = 'zh-cn' } = options;

  // 处理无效输入
  if (seconds === undefined || seconds === null || isNaN(seconds)) {
    return '-';
  }

  // 处理负数和零
  if (seconds <= 0) {
    return lang === 'en' ? '0s' : '0秒';
  }

  // 使用 dayjs duration 计算
  const durationObj = dayjs.duration(seconds, 'seconds');

  const days = Math.floor(durationObj.asDays());
  const hours = durationObj.hours();
  const minutes = durationObj.minutes();
  const secs = durationObj.seconds();

  const parts: string[] = [];

  // 根据语言选择单位
  const units =
    lang === 'en'
      ? { day: 'd', hour: 'h', minute: 'm', second: 's', separator: ' ' }
      : { day: '天', hour: '小时', minute: '分钟', second: '秒', separator: '' };

  // 根据不同情况组合返回值
  if (days > 0) {
    parts.push(`${days}${units.day}`);
  }

  if (hours > 0) {
    parts.push(`${hours}${units.hour}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}${units.minute}`);
  }

  if (secs > 0) {
    parts.push(`${secs}${units.second}`);
  }

  // 如果所有部分都是0（理论上不会发生，因为已经处理了 <= 0 的情况）
  return parts.length > 0 ? parts.join(units.separator) : lang === 'en' ? '0s' : '0秒';
}
